import React, { useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';
import ReactEcharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { LineChart as LineChartType } from '../../../../types/charts';
import { useEChartsTheme, getChartColors, useTheme } from '../../../../theme';
import { chartImageExtractor } from '../../../../helpers/chartImageExtractor';

interface LineChartProps {
  chartData: LineChartType;
  width?: number;
  height?: number;
  chartId?: string;
}

const LineChart: React.FC<LineChartProps> = ({ chartData, width = 400, height = 300, chartId }) => {
  const theme = useTheme();
  const echartsTheme = useEChartsTheme();
  const chartRef = useRef<any>(null);

  const option: EChartsOption = useMemo(() => {
    const { data, config, title } = chartData;
    const defaultColors = getChartColors(theme, 'default');

    return {
      title: title
        ? {
            text: title,
            left: 'left',
            textStyle: {
              fontSize: 16,
              fontWeight: 500,
              color: echartsTheme.textColor
            }
          }
        : undefined,
      tooltip: {
        trigger: 'axis',
        backgroundColor: echartsTheme.tooltipBg,
        borderColor: echartsTheme.borderColor,
        borderWidth: 1,
        textStyle: {
          color: echartsTheme.textColor
        },
        confine: true,
        z: 9999
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: data.map((item) => item.name),
        axisLine: {
          lineStyle: {
            color: echartsTheme.borderColor
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: echartsTheme.textColor
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: echartsTheme.borderColor
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: echartsTheme.textColor
        },
        splitLine: {
          lineStyle: {
            color: echartsTheme.splitLineColor
          }
        }
      },
      series: [
        {
          type: 'line',
          data: data.map((item) => ({
            value: item.value,
            name: item.name,
            itemStyle: {
              color: item.color || config?.colors?.[0] || defaultColors[0]
            }
          })),
          smooth: config?.smooth || true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            width: 3,
            color: config?.colors?.[0] || defaultColors[0]
          },
          areaStyle: config?.showArea
            ? {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    {
                      offset: 0,
                      color: config?.colors?.[0] || defaultColors[0]
                    },
                    {
                      offset: 1,
                      color: 'rgba(84, 112, 198, 0.1)'
                    }
                  ]
                }
              }
            : undefined,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.3)'
            }
          }
        }
      ]
    };
  }, [chartData, theme, echartsTheme]);

  // Register chart instance for image extraction
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (chartId) {
        chartImageExtractor.unregisterChart(chartId);
      }
    };
  }, [chartId]);

  const onChartReady = (chart: any) => {
    if (chartId) {
      chartImageExtractor.registerChart(chartId, chart);
    }
  };

  return (
    <ChartContainer>
      <ReactEcharts
        ref={chartRef}
        option={option}
        style={{ width: `${width}px`, height: `${height}px` }}
        opts={{ renderer: 'canvas' }}
        onChartReady={onChartReady}
      />
    </ChartContainer>
  );
};

export default LineChart;

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;
